import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Phone,
  CreditCard,
  Calendar,
  Filter,
  Trash2,
  FileText,
  User,
  Loader,
  Search,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order, OrderItem } from '../../services/dataService';
import { updateOrderWithItems, removeItemsFromOrder } from '../../utils/orderUtils';
import { createCheckoutIdempotencyKey } from '../../utils/checkoutIdempotency';
import AddOrderModal from '../orders/AddOrderModal';
import AddItemToOrderModal from '../orders/AddItemToOrderModal';
import PaymentModal from '../orders/PaymentModal';
import WhatsAppReceiptButton from '../orders/WhatsAppReceiptButton';
import PrintReceiptButton from '../orders/PrintReceiptButton';

const Orders: React.FC = () => {
  const { tenantData } = useAuth();
  
  // Check if POS Simplified mode is enabled
  const posSimplified = tenantData?.posSimplified || false;
  
  // Set default tab based on simplified mode
  const [activeTab, setActiveTab] = useState(posSimplified ? 'completed' : 'requested');
  const [dateFilter, setDateFilter] = useState<'today' | 'range'>('today');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [completedFilter, setCompletedFilter] = useState<'all' | 'due'>('all');
  const [completedSearchTerm, setCompletedSearchTerm] = useState('');
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [requestedItems, setRequestedItems] = useState<{ order: Order; item: OrderItem }[]>([]);
  const [preparingItems, setPreparingItems] = useState<{ order: Order; item: OrderItem }[]>([]);
  const [completedItems, setCompletedItems] = useState<{ order: Order; item: OrderItem }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderToAddItems, setOrderToAddItems] = useState<Order | null>(null);
  
  // Add state for split view
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<Order | null>(null);
  const [selectedOrderItems, setSelectedOrderItems] = useState<OrderItem[]>([]);

  // Add state for mobile view
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  
  // Add state for kitchen view collapse
  const [isKitchenViewCollapsed, setIsKitchenViewCollapsed] = useState(false);
  
  // Add state for screen size detection
  const [isMobile, setIsMobile] = useState(false);
  
  // Add state for search expansion on mobile
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Optimistic UI State
  const [pendingOrders, setPendingOrders] = useState<Set<string>>(new Set());
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const [syncedOrders, setSyncedOrders] = useState<Set<string>>(new Set());
  const [optimisticOrders, setOptimisticOrders] = useState<Order[]>([]);

  // Listen for navbar actions
  useEffect(() => {
    const handleNavbarAction = (event: CustomEvent) => {
      const action = event.detail;
      if (action === 'new-order') {
        setShowAddModal(true);
      }
    };

    window.addEventListener('navbar-action', handleNavbarAction as EventListener);
    
    return () => {
      window.removeEventListener('navbar-action', handleNavbarAction as EventListener);
    };
  }, []);

  // Detect screen size changes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Force activeTab to 'completed' when in simplified mode
  useEffect(() => {
    if (posSimplified && activeTab !== 'completed') {
      setActiveTab('completed');
    }
  }, [posSimplified, activeTab]);

  // Optimistic UI Functions
  const showErrorToast = (message: string) => {
    // You can replace this with your preferred toast library
    console.error('Order Error:', message);
    // TODO: Replace with proper toast notification
    alert(message); // Temporary fallback
  };

  const showSuccessToast = (message: string) => {
    console.log('Success:', message);
    // You can add success toast here if needed
  };

  const addOrderOptimistically = useCallback(async (
    customer: string,
    customerPhone: string | undefined,
    items: Array<{
      menuItem: any;
      quantity: number;
      notes: string;
      addOnSelections?: Array<{
        categoryId: string;
        categoryName: string;
        selectedOptions: Array<{
          optionId: string;
          optionName: string;
          price: number;
        }>;
      }>;
    }>,
    currentTab: 'requested' | 'preparing' | 'completed',
    tableNumber?: string,
    tableId?: string, 
    notes?: string,
    paymentMethod?: 'cash' | 'card' | 'digital',
    idempotencyKey?: string
  ) => {
    // Generate temporary ID
    const tempOrderId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create order items with consistent totalPrice calculation
    const orderItems: OrderItem[] = items.map((item, index) => {
      let totalPrice = item.menuItem.price * item.quantity;
      
      // Calculate add-ons pricing
      if (item.addOnSelections) {
        item.addOnSelections.forEach(category => {
          category.selectedOptions.forEach(option => {
            totalPrice += option.price * item.quantity;
          });
        });
      }
      
      return {
        id: `${tempOrderId}-item-${index}`,
        menuItemId: item.menuItem.id!,
        menuItemName: item.menuItem.name,
        quantity: item.quantity,
        unitPrice: item.menuItem.price,
        totalPrice: totalPrice,
        status: currentTab,
        notes: item.notes.trim() || undefined,
        addOnSelections: item.addOnSelections // Include add-ons data
      };
    });

    // Calculate totals
    const subtotal = orderItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const total = subtotal; // No tax

    // Create optimistic order
    const isPaidWithCash = paymentMethod === 'cash';
    const optimisticOrder: Order = {
      id: tempOrderId,
      customer: customer.trim(),
      customerId: customer === 'Guest' ? '' : '', // No customer ID for guest orders
      customerPhone: customer === 'Guest' ? undefined : customerPhone?.trim(),
      items: orderItems,
      subtotal,
      tax: 0,
      total,
      status: 'active',
      isPaid: isPaidWithCash,
      paidAmount: isPaidWithCash ? total : 0,
      remainingAmount: isPaidWithCash ? 0 : total,
      tableNumber: tableNumber?.trim(),
      notes: notes?.trim(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // 1. Immediately add to optimistic state AND start Firebase sync in parallel
    console.log(`🚀 [${Date.now()}] STARTING optimistic order creation for ${customer}`);
    setOptimisticOrders(prev => [optimisticOrder, ...prev]);
    setPendingOrders(prev => new Set([...Array.from(prev), tempOrderId]));
    
    // Auto-select the new order
    setSelectedOrderForDetail(optimisticOrder);
    const currentTabItems = optimisticOrder.items?.filter(item => item.status === activeTab) || [];
    setSelectedOrderItems(currentTabItems);

    // 2. IMMEDIATELY start Firebase operations (don't await - run in parallel)
    console.log(`⚡ [${Date.now()}] IMMEDIATELY starting Firebase sync for ${customer}`);
    const firebaseSync = async () => {
      try {
        console.log(`🔥 [${Date.now()}] Firebase sync STARTED - getting/creating order for ${customer}`);
        
        const checkoutOptions = idempotencyKey ? { idempotencyKey } : undefined;

        // Handle guest orders vs regular orders
        let actualOrder: Order;
        if (customer === 'Guest') {
          actualOrder = await dataService.createGuestOrder(
            tenantData!.id,
            tableNumber?.trim() || undefined,
            tableId,
            notes?.trim() || undefined,
            checkoutOptions
          );
          console.log(`📦 [${Date.now()}] Created guest order ${actualOrder.id}, adding ${items.length} items`);
        } else {
          // Get or create active order for customer
          // Only pass customerPhone if it's not empty
          const phoneNumber = customerPhone?.trim();
          actualOrder = await dataService.getOrCreateActiveOrder(
            tenantData!.id,
            customer.trim(),
            phoneNumber || undefined,
            tableNumber?.trim() || undefined,
            tableId,
            checkoutOptions
          );
          console.log(`📦 [${Date.now()}] Got order ${actualOrder.id} for ${customer}, adding ${items.length} items`);
        }

        // Prepare all items for batch addition
        const orderItems: OrderItem[] = items.map((item, index) => {
          let totalPrice = item.menuItem.price * item.quantity;
          
          // Calculate add-ons pricing
          if (item.addOnSelections) {
            item.addOnSelections.forEach(category => {
              category.selectedOptions.forEach(option => {
                totalPrice += option.price * item.quantity;
              });
            });
          }
          
          // Create a unique identifier that includes add-ons for proper tracking
          const addOnsSignature = item.addOnSelections 
            ? item.addOnSelections.map(cat => 
                `${cat.categoryId}:${cat.selectedOptions.map(opt => opt.optionId).sort().join(',')}`
              ).sort().join('|')
            : 'no-addons';
          
          const orderItem: OrderItem = {
            id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
            menuItemId: item.menuItem.id!,
            menuItemName: item.menuItem.name,
            quantity: item.quantity,
            unitPrice: item.menuItem.price,
            totalPrice: totalPrice,
            status: currentTab,
            addOnSelections: item.addOnSelections, // Include add-ons data
            addOnsSignature: addOnsSignature // Include add-ons signature for tracking
          };

          if (item.notes && item.notes.trim()) {
            orderItem.notes = item.notes.trim();
          }

          return orderItem;
        });

        // Add all items to the order in one batch operation
        console.log(`🚀 Adding ${orderItems.length} items to order ${actualOrder.id} in batch`);
        await dataService.addMultipleItemsToOrder(tenantData!.id, actualOrder.id!, orderItems);

        // Update order with additional info if provided
        if (tableNumber || notes) {
          const updateData: Partial<Order> = {};
          
          if (tableNumber?.trim()) {
            updateData.tableNumber = tableNumber.trim();
          }
          
          if (notes?.trim()) {
            updateData.notes = notes.trim();
          }
          
          if (Object.keys(updateData).length > 0) {
            await dataService.update<Order>(tenantData!.id, 'orders', actualOrder.id!, updateData);
          }
        }

        // Process payment if cash is selected
        if (paymentMethod === 'cash') {
          const orderTotal = orderItems.reduce((sum, item) => sum + item.totalPrice, 0);
          await dataService.addPaymentToOrder(
            tenantData!.id,
            actualOrder.id!,
            orderTotal,
            'cash',
            undefined,
            { idempotencyKey: createCheckoutIdempotencyKey('payment') }
          );
        }

        // Success - show as synced for 1 second
        setPendingOrders(prev => {
          const updated = new Set(prev);
          updated.delete(tempOrderId);
          return updated;
        });

        setSyncedOrders(prev => new Set([...Array.from(prev), tempOrderId]));

        // Clear synced state after 1 second (3-second timer will handle cleanup)
        setTimeout(() => {
          setSyncedOrders(prev => {
            const updated = new Set(prev);
            updated.delete(tempOrderId);
            return updated;
          });
        }, 1000);

        console.log(`✅ [${Date.now()}] Firebase sync COMPLETED for ${customer}`);
        showSuccessToast('Order created successfully!');
      } catch (error) {
        console.error('Failed to create order:', error);
        
        // Failed - remove optimistic order and show error
        setOptimisticOrders(prev => prev.filter(o => o.id !== tempOrderId));
        setPendingOrders(prev => {
          const updated = new Set(prev);
          updated.delete(tempOrderId);
          return updated;
        });
        
        showErrorToast('Failed to create order. Please try again.');
        throw error; // Re-throw so modal can handle it
      }
    };

    // Start Firebase sync immediately (don't await - let it run in background)
    console.log(`🎯 [${Date.now()}] TRIGGERING Firebase sync NOW (non-blocking)`);
    firebaseSync().catch(error => {
      console.error('Firebase sync failed:', error);
      // Error handling is already done inside firebaseSync
    });
    console.log(`💨 [${Date.now()}] Firebase sync triggered, continuing immediately...`);
  }, [tenantData, activeTab]);

  const addItemsToOrderOptimistically = useCallback(async (
    orderId: string,
    items: Array<{
      menuItem: any;
      quantity: number;
      notes: string;
      addOnSelections?: Array<{
        categoryId: string;
        categoryName: string;
        selectedOptions: Array<{
          optionId: string;
          optionName: string;
          price: number;
        }>;
      }>;
    }>,
    currentTab: 'requested' | 'preparing' | 'completed',
    paymentMethod?: 'cash' | 'card' | 'digital'
  ) => {
    // Generate temporary item IDs
    const tempItemIds = items.map((_, index) => `temp-item-${Date.now()}-${index}`);
    
            // Create order items with consistent totalPrice calculation
        const newOrderItems: OrderItem[] = items.map((item, index) => {
          let totalPrice = item.menuItem.price * item.quantity;
          
          // Calculate add-ons pricing
          if (item.addOnSelections) {
            item.addOnSelections.forEach(category => {
              category.selectedOptions.forEach(option => {
                totalPrice += option.price * item.quantity;
              });
            });
          }
          
          // Create a unique identifier that includes add-ons for proper tracking
          const addOnsSignature = item.addOnSelections 
            ? item.addOnSelections.map(cat => 
                `${cat.categoryId}:${cat.selectedOptions.map(opt => opt.optionId).sort().join(',')}`
              ).sort().join('|')
            : 'no-addons';
          
          return {
            id: tempItemIds[index],
            menuItemId: item.menuItem.id!,
            menuItemName: item.menuItem.name,
            quantity: item.quantity,
            unitPrice: item.menuItem.price,
            totalPrice: totalPrice,
            status: currentTab,
            notes: item.notes.trim() || undefined,
            addOnSelections: item.addOnSelections, // Include add-ons data
            // Add a unique signature for add-ons to help with tracking
            addOnsSignature: addOnsSignature
          };
        });

    // 1. Immediately update optimistic state using utility function
    setAllOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return updateOrderWithItems(order, newOrderItems);
      }
      return order;
    }));

    // Mark items as pending
    tempItemIds.forEach(id => {
      setPendingItems(prev => new Set([...Array.from(prev), id]));
    });

    // 2. IMMEDIATELY start Firebase operations (don't await - run in parallel)
    const firebaseSync = async () => {
      try {
        // Prepare all items for batch addition
        const orderItems: OrderItem[] = items.map((item, index) => {
          let totalPrice = item.menuItem.price * item.quantity;
          
          // Calculate add-ons pricing
          if (item.addOnSelections) {
            item.addOnSelections.forEach(category => {
              category.selectedOptions.forEach(option => {
                totalPrice += option.price * item.quantity;
              });
            });
          }
          
          // Create a unique identifier that includes add-ons for proper tracking
          const addOnsSignature = item.addOnSelections 
            ? item.addOnSelections.map(cat => 
                `${cat.categoryId}:${cat.selectedOptions.map(opt => opt.optionId).sort().join(',')}`
              ).sort().join('|')
            : 'no-addons';
          
          const orderItem: OrderItem = {
            id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
            menuItemId: item.menuItem.id!,
            menuItemName: item.menuItem.name,
            quantity: item.quantity,
            unitPrice: item.menuItem.price,
            totalPrice: totalPrice,
            status: currentTab,
            addOnSelections: item.addOnSelections, // Include add-ons data
            addOnsSignature: addOnsSignature // Include add-ons signature for tracking
          };

          if (item.notes && item.notes.trim()) {
            orderItem.notes = item.notes.trim();
          }

          return orderItem;
        });

        // Add all items to the order in one batch operation
        console.log(`🚀 Adding ${orderItems.length} items to existing order ${orderId} in batch`);
        await dataService.addMultipleItemsToOrder(tenantData!.id, orderId, orderItems);

        // Process payment if a payment method is selected (excluding digital/paylater)
        if (paymentMethod && paymentMethod !== 'digital') {
          const orderTotal = orderItems.reduce((sum, item) => sum + item.totalPrice, 0);
          console.log(`💳 Processing ${paymentMethod} payment of ₹${orderTotal} for order ${orderId}`);
          await dataService.addPaymentToOrder(
            tenantData!.id,
            orderId,
            orderTotal,
            paymentMethod,
            undefined,
            { idempotencyKey: createCheckoutIdempotencyKey('payment') }
          );
        }

        // Success - remove pending state (real-time subscription will update the data)
        tempItemIds.forEach(id => {
          setPendingItems(prev => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
        });

        showSuccessToast('Items added successfully!');
      } catch (error) {
        console.error('Failed to add items to order:', error);
        
        // Failed - revert optimistic updates using utility function
        setAllOrders(prev => prev.map(order => {
          if (order.id === orderId) {
            return removeItemsFromOrder(order, tempItemIds);
          }
          return order;
        }));

        // Remove pending state
        tempItemIds.forEach(id => {
          setPendingItems(prev => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
        });
        
        showErrorToast('Failed to add items to order. Please try again.');
        throw error; // Re-throw so modal can handle it
      }
    };

    // Start Firebase sync immediately (don't await - let it run in background)
    firebaseSync().catch(error => {
      console.error('Firebase sync failed:', error);
      // Error handling is already done inside firebaseSync
    });
  }, [tenantData]);

    // Combine real orders with optimistic orders (time-based cleanup handles duplicates)
  const getCombinedOrders = useCallback(() => {
    // Create a map to track orders by customer and time to prevent duplicates
    const orderMap = new Map<string, Order>();
    
    // First, add all real orders
    allOrders.forEach(order => {
      const key = `${order.customer}-${new Date(order.createdAt!).getTime()}`;
      orderMap.set(key, order);
    });
    
    // Then add optimistic orders, but only if they don't have a matching real order
    optimisticOrders.forEach(optimisticOrder => {
      const key = `${optimisticOrder.customer}-${new Date(optimisticOrder.createdAt!).getTime()}`;
      
      // Only add if no real order exists for this customer at this time
      if (!orderMap.has(key)) {
        orderMap.set(key, optimisticOrder);
      } else {
        // If a real order exists, remove the optimistic order from state
        console.log(`🔄 Removing duplicate optimistic order for ${optimisticOrder.customer}`);
        setOptimisticOrders(prev => prev.filter(o => o.id !== optimisticOrder.id));
        setPendingOrders(prev => {
          const updated = new Set(prev);
          updated.delete(optimisticOrder.id!);
          return updated;
        });
        setSyncedOrders(prev => {
          const updated = new Set(prev);
          updated.delete(optimisticOrder.id!);
          return updated;
        });
      }
    });
    
    // Convert map back to array and sort by creation time (newest first)
    return Array.from(orderMap.values()).sort((a, b) => 
      new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
    );
  }, [optimisticOrders, allOrders]);

    // Auto-cleanup optimistic orders after 6 seconds (let all items be added first)
  useEffect(() => {
    if (optimisticOrders.length > 0) {
      optimisticOrders.forEach(optimisticOrder => {
        // Set a 3-second timer for each optimistic order
        const orderId = optimisticOrder.id!;
        const orderTime = new Date(optimisticOrder.createdAt!).getTime();
        const now = Date.now();
        const ageInSeconds = (now - orderTime) / 1000;
        
        // If order is already 6+ seconds old, clean it up immediately
        if (ageInSeconds >= 6) {
          // Check if this optimistic order was selected, find replacement
          const wasSelected = selectedOrderForDetail?.id === orderId;
          let replacementOrder = null;
          
          if (wasSelected) {
            // Find the real order for the same customer
            replacementOrder = allOrders.find(realOrder => 
              realOrder.customer === optimisticOrder.customer &&
              Math.abs(new Date(realOrder.createdAt!).getTime() - orderTime) <= 60000
            );
          }
          
          setOptimisticOrders(prev => prev.filter(o => o.id !== orderId));
          setPendingOrders(prev => {
            const updated = new Set(prev);
            updated.delete(orderId);
            return updated;
          });
          setSyncedOrders(prev => {
            const updated = new Set(prev);
            updated.delete(orderId);
            return updated;
          });
          
          // Update selection to real order if needed
          if (wasSelected && replacementOrder) {
            setSelectedOrderForDetail(replacementOrder);
            const currentTabItems = replacementOrder.items?.filter(item => item.status === activeTab) || [];
            setSelectedOrderItems(currentTabItems);
          }
          
          console.log(`🧹 Cleaned up optimistic order ${orderId} (age: ${ageInSeconds.toFixed(1)}s)`);
        } else {
          // Set timer for remaining time
          const remainingTime = (6 - ageInSeconds) * 1000;
          setTimeout(() => {
            // Check if this optimistic order was selected, find replacement
            const wasSelected = selectedOrderForDetail?.id === orderId;
            let replacementOrder = null;
            
            if (wasSelected) {
              // Find the real order for the same customer
              replacementOrder = allOrders.find(realOrder => 
                realOrder.customer === optimisticOrder.customer &&
                Math.abs(new Date(realOrder.createdAt!).getTime() - orderTime) <= 60000
              );
            }
            
            setOptimisticOrders(prev => prev.filter(o => o.id !== orderId));
            setPendingOrders(prev => {
              const updated = new Set(prev);
              updated.delete(orderId);
              return updated;
            });
            setSyncedOrders(prev => {
              const updated = new Set(prev);
              updated.delete(orderId);
              return updated;
            });
            
            // Update selection to real order if needed
            if (wasSelected && replacementOrder) {
              setSelectedOrderForDetail(replacementOrder);
              const currentTabItems = replacementOrder.items?.filter(item => item.status === activeTab) || [];
              setSelectedOrderItems(currentTabItems);
            }
            
            console.log(`🧹 Cleaned up optimistic order ${orderId} after 6 seconds`);
          }, remainingTime);
        }
      });
    }
  }, [optimisticOrders, selectedOrderForDetail, allOrders, activeTab]);

  const tabs = [
    {
      id: 'requested',
      name: 'Requested',
      icon: AlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      id: 'preparing',
      name: 'Preparing',
      icon: Clock,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100'
    },
    {
      id: 'completed',
      name: 'Completed',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ];

  const filterOrdersByDate = useCallback((orders: Order[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let filteredOrders: Order[] = [];
    
    if (dateFilter === 'today') {
      // Filter for today's orders
      filteredOrders = orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime();
      });
    } else {
      // Filter for date range
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      filteredOrders = orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= start && orderDate <= end;
      });
    }

    return filteredOrders;
  }, [dateFilter, startDate, endDate]);

  const processFilteredOrders = useCallback((filteredOrders: Order[]) => {
    const requestedData: { order: Order; item: OrderItem }[] = [];
    const preparingData: { order: Order; item: OrderItem }[] = [];
    const completedData: { order: Order; item: OrderItem }[] = [];

    filteredOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const orderItem = { order, item };
          switch (item.status) {
            case 'requested':
              requestedData.push(orderItem);
              break;
            case 'preparing':
              preparingData.push(orderItem);
              break;
            case 'completed':
              completedData.push(orderItem);
              break;
          }
        });
      }
    });

    setRequestedItems(requestedData);
    setPreparingItems(preparingData);
    setCompletedItems(completedData);
  }, []);

  // Effect to filter orders when data or filters change - now uses combined orders
  useEffect(() => {
    const combinedOrders = getCombinedOrders();
    if (combinedOrders.length > 0) {
      const filteredOrders = filterOrdersByDate(combinedOrders);
      processFilteredOrders(filteredOrders);
    } else {
      setRequestedItems([]);
      setPreparingItems([]);
      setCompletedItems([]);
    }
  }, [allOrders, optimisticOrders, filterOrdersByDate, processFilteredOrders, getCombinedOrders]);

  // Set up real-time subscription for orders
  useEffect(() => {
    if (!tenantData?.id) return;

    console.log('Setting up real-time orders subscription...');
    setLoading(true);

    const unsubscribe = dataService.subscribeToOrders(tenantData.id, (orders) => {
      console.log('Orders updated:', orders.length);
      setAllOrders(orders || []);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up orders subscription...');
      unsubscribe();
    };
  }, [tenantData?.id]);

  // Reset selected order when tab changes
  useEffect(() => {
    setSelectedOrderForDetail(null);
    setSelectedOrderItems([]);
    setIsMobileExpanded(false); // Reset mobile expansion
    // Reset completed filter when switching away from completed tab
    if (activeTab !== 'completed') {
      setCompletedFilter('all');
    }
  }, [activeTab]);

  // Reset selected order when completed filter changes
  useEffect(() => {
    if (activeTab === 'completed') {
      setSelectedOrderForDetail(null);
      setSelectedOrderItems([]);
    }
  }, [completedFilter, activeTab]);

  // Update selected order details when data changes (for live updates)
  useEffect(() => {
    if (selectedOrderForDetail && allOrders.length > 0) {
      // Find the updated order in the current data
      const combinedOrders = getCombinedOrders();
      const updatedOrder = combinedOrders.find(order => order.id === selectedOrderForDetail.id);
      
      if (updatedOrder) {
        // Update the selected order with fresh data
        setSelectedOrderForDetail(updatedOrder);
        
        // Get the items for the current tab from the updated order
        const currentTabItems = updatedOrder.items?.filter(item => item.status === activeTab) || [];
        setSelectedOrderItems(currentTabItems);
      } else {
        // Order might have been deleted or moved to a different status
        setSelectedOrderForDetail(null);
        setSelectedOrderItems([]);
      }
    }
  }, [allOrders, optimisticOrders, selectedOrderForDetail, activeTab, getCombinedOrders]);

  // Auto-select first order when tab changes and no order is selected
  useEffect(() => {
    // Only auto-select if no order is currently selected
    if (!selectedOrderForDetail) {
      // Get current tab items
      const currentTabItems = getCurrentItems();
      if (currentTabItems.length > 0) {
        // Group by order and select first
        const groupedByOrder = currentTabItems.reduce((acc, { order, item }) => {
          const orderId = order.id!;
          if (!acc[orderId]) {
            acc[orderId] = { order, items: [] };
          }
          acc[orderId].items.push(item);
          return acc;
        }, {} as Record<string, { order: Order; items: OrderItem[] }>);
        
        const orderGroups = Object.values(groupedByOrder);
        if (orderGroups.length > 0) {
          const firstOrder = orderGroups[0];
          setSelectedOrderForDetail(firstOrder.order);
          setSelectedOrderItems(firstOrder.items);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderForDetail, allOrders, activeTab, requestedItems, preparingItems, completedItems, completedFilter]);

  // Force auto-selection when tab changes (separate effect to ensure it runs after items are processed)
  useEffect(() => {
    // Use setTimeout to ensure this runs after the state updates
    const timeoutId = setTimeout(() => {
      const currentTabItems = getCurrentItems();
      if (currentTabItems.length > 0) {
        // Check if the currently selected order has items in the current tab
        const selectedOrderHasItemsInCurrentTab = selectedOrderForDetail && 
          currentTabItems.some(({ order }) => order.id === selectedOrderForDetail.id);
        
        // If no order is selected OR the selected order has no items in current tab, auto-select first order
        if (!selectedOrderForDetail || !selectedOrderHasItemsInCurrentTab) {
          const groupedByOrder = currentTabItems.reduce((acc, { order, item }) => {
            const orderId = order.id!;
            if (!acc[orderId]) {
              acc[orderId] = { order, items: [] };
            }
            acc[orderId].items.push(item);
            return acc;
          }, {} as Record<string, { order: Order; items: OrderItem[] }>);
          
          const orderGroups = Object.values(groupedByOrder);
          if (orderGroups.length > 0) {
            const firstOrder = orderGroups[0];
            setSelectedOrderForDetail(firstOrder.order);
            setSelectedOrderItems(firstOrder.items);
          }
        } else if (selectedOrderForDetail) {
          // Update the selected items to match the current tab
          const selectedOrderItems = currentTabItems
            .filter(({ order }) => order.id === selectedOrderForDetail.id)
            .map(({ item }) => item);
          setSelectedOrderItems(selectedOrderItems);
        }
      } else {
        // No items in current tab, clear selection
        setSelectedOrderForDetail(null);
        setSelectedOrderItems([]);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, completedFilter]);

  const handleStatusChange = async (orderId: string, itemId: string, newStatus: OrderItem['status']) => {
    try {
      await dataService.updateOrderItemStatus(tenantData!.id, orderId, itemId, newStatus);
      // Data will update automatically via real-time subscription
    } catch (error) {
      console.error('Error updating item status:', error);
    }
  };

  const handleDeleteItem = async (orderId: string, itemId: string, itemName: string) => {
    if (window.confirm(`Are you sure you want to delete "${itemName}" from this order?`)) {
      try {
        await dataService.deleteOrderItem(tenantData!.id, orderId, itemId);
        // Data will update automatically via real-time subscription
      } catch (error) {
        console.error('Error deleting order item:', error);
        alert('Failed to delete order item. Please try again.');
      }
    }
  };

  const handlePayment = (order: Order) => {
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const handleAddOrder = () => {
    // Modal will handle optimistic updates internally
    setShowAddModal(false);
  };

  const handlePaymentComplete = () => {
    // Modal closes itself immediately after successful payment processing
    // Real-time subscriptions will handle data updates automatically
    setShowPaymentModal(false);
    setSelectedOrder(null);
  };

  const handlePhoneUpdate = async (orderId: string, phone: string) => {
    try {
      await dataService.update<Order>(tenantData!.id, 'orders', orderId, {
        customerPhone: phone
      });
      // Data will update automatically via real-time subscription
    } catch (error) {
      console.error('Error updating customer phone:', error);
      throw error;
    }
  };

  const handleAddItemsToOrder = (order: Order) => {
    setOrderToAddItems(order);
    setShowAddItemModal(true);
  };

  const handleAddItemSuccess = () => {
    // Modal will handle optimistic updates internally
    setShowAddItemModal(false);
    setOrderToAddItems(null);
  };

  const renderOrderItems = (items: { order: Order; item: OrderItem }[]) => {
    if (items.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            {activeTab === 'requested' && <AlertCircle className="w-16 h-16 mx-auto" />}
            {activeTab === 'preparing' && <Clock className="w-16 h-16 mx-auto" />}
            {activeTab === 'completed' && <CheckCircle className="w-16 h-16 mx-auto" />}
          </div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
            No {activeTab} items
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Items will appear here as orders are placed and processed.
          </p>
        </div>
      );
    }

    // Group items by order ID
    const groupedByOrder = items.reduce((acc, { order, item }) => {
      const orderId = order.id!;
      if (!acc[orderId]) {
        acc[orderId] = {
          order,
          items: []
        };
      }
      acc[orderId].items.push(item);
      return acc;
    }, {} as Record<string, { order: Order; items: OrderItem[] }>);

    const orderGroups = Object.values(groupedByOrder);

    // Auto-select first order if none selected and we have orders
    // This ensures we always show an order when switching tabs
    if (!selectedOrderForDetail && orderGroups.length > 0) {
      const firstOrder = orderGroups[0];
      setSelectedOrderForDetail(firstOrder.order);
      setSelectedOrderItems(firstOrder.items);
    }

    return (
      <div className="space-y-4">
        {/* Quick View for Preparing Tab - Hidden in Simplified Mode */}
        {!posSimplified && activeTab === 'preparing' && items.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            {/* Mobile Header Layout */}
            <div className="block tablet:hidden">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <Clock className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">Kitchen Quick View</h3>
                  <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 rounded-full text-xs font-medium flex-shrink-0">
                    {items.length}
                  </span>
                </div>
                <button
                  onClick={() => setIsKitchenViewCollapsed(!isKitchenViewCollapsed)}
                  className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0 ml-2"
                  title={isKitchenViewCollapsed ? 'Expand kitchen view' : 'Collapse kitchen view'}
                >
                  <span className="text-xs font-medium">
                    {isKitchenViewCollapsed ? 'Show' : 'Hide'}
                  </span>
                  <div className={`transform transition-transform duration-200 ${
                    isKitchenViewCollapsed ? 'rotate-180' : ''
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </div>
                </button>
              </div>
              {!isKitchenViewCollapsed && (
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  All items currently being prepared
                </div>
              )}
            </div>

            {/* Tablet+ Header Layout */}
            <div className="hidden tablet:flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kitchen Quick View</h3>
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 rounded-full text-sm font-medium">
                  {items.length} items to prepare
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  All items currently being prepared
                </div>
                <button
                  onClick={() => setIsKitchenViewCollapsed(!isKitchenViewCollapsed)}
                  className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={isKitchenViewCollapsed ? 'Expand kitchen view' : 'Collapse kitchen view'}
                >
                  <span className="text-xs font-medium">
                    {isKitchenViewCollapsed ? 'Show' : 'Hide'}
                  </span>
                  <div className={`transform transition-transform duration-200 ${
                    isKitchenViewCollapsed ? 'rotate-180' : ''
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Collapsible Content */}
            {!isKitchenViewCollapsed && (
              <>
                {/* Consolidated Items List */}
                <div className="grid grid-cols-2 tablet:grid-cols-3 desktop:grid-cols-4 gap-2">
                  {items.map(({ order, item }) => (
                    <div key={`${order.id}-${item.id}`} className="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-md p-2 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors duration-150">
                      {/* Main Item Info - Compact */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex-1 min-w-0 mr-2">
                          <h4 className="font-bold text-gray-900 dark:text-white text-xs truncate leading-tight">
                            {item.menuItemName}
                          </h4>
                          <div className="flex items-center space-x-1 mt-0.5">
                            <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">Qty</span>
                            <span className="font-bold text-primary-600 dark:text-primary-400 text-sm">{item.quantity}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleStatusChange(order.id!, item.id, 'completed')}
                          className="bg-green-600 hover:bg-green-700 text-white p-1 rounded text-xs font-medium transition-colors duration-200 flex-shrink-0"
                          title="Mark as completed"
                        >
                          <CheckCircle className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {/* Customer Info - Compressed */}
                      <div className="border-t border-primary-200 dark:border-primary-700 pt-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 flex-1 min-w-0">
                            <User className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-700 dark:text-gray-300 text-xs truncate">{order.customer}</span>
                          </div>
                          {order.tableNumber && (
                            <span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-medium ml-1">
                              {order.tableNumber}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            #{order.id?.slice(-4)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {order.createdAt instanceof Date 
                              ? order.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : new Date(order.createdAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            }
                          </span>
                        </div>
                        
                        {item.notes && (
                          <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded px-1 py-0.5 mt-1">
                            <p className="text-xs text-yellow-800 dark:text-yellow-200 font-medium">
                              {item.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Items Summary */}
                {(() => {
                  // Calculate totals for each menu item
                  const itemTotals = items.reduce((acc, { item }) => {
                    const itemName = item.menuItemName;
                    acc[itemName] = (acc[itemName] || 0) + item.quantity;
                    return acc;
                  }, {} as Record<string, number>);

                  const sortedItems = Object.entries(itemTotals).sort(([, a], [, b]) => b - a);

                  return (
                    <div className="mt-3 pt-3 border-t border-primary-200 dark:border-primary-700">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Items Summary</h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {sortedItems.length} type{sortedItems.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 tablet:grid-cols-3 desktop:grid-cols-4 gap-1">
                        {sortedItems.map(([itemName, total]) => (
                          <div key={itemName} className="bg-white dark:bg-gray-700 border border-primary-300 dark:border-primary-600 rounded px-2 py-1 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate flex-1 mr-1">
                              {itemName}
                            </span>
                            <span className="text-xs font-bold text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/50 px-1 py-0.5 rounded flex-shrink-0">
                              {total}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Quick Actions */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Mobile Layout */}
                  <div className="block tablet:hidden space-y-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
                      Tap items above to mark complete
                    </div>
                    <button
                      onClick={() => {
                        // Mark all preparing items as completed
                        items.forEach(({ order, item }) => {
                          handleStatusChange(order.id!, item.id, 'completed');
                        });
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2 text-sm"
                      disabled={items.length === 0}
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Complete All Items</span>
                    </button>
                  </div>

                  {/* Tablet+ Layout */}
                  <div className="hidden tablet:flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Click individual items to mark as completed, or use detailed view below for more options.
                    </div>
                    <button
                      onClick={() => {
                        // Mark all preparing items as completed
                        items.forEach(({ order, item }) => {
                          handleStatusChange(order.id!, item.id, 'completed');
                        });
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 text-sm"
                      disabled={items.length === 0}
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Complete All Items</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Detailed Order View */}
        {/* Mobile View - Collapsible */}
        <div className="block tablet:hidden">
          {/* Orders List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
            {!posSimplified && (
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Orders
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {orderGroups.length} order{orderGroups.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            
            <div className="space-y-3 p-3">
              {orderGroups.map(({ order, items }, index) => {
                const isSelected = selectedOrderForDetail?.id === order.id;
                const isExpanded = isSelected && isMobileExpanded;
                const totalItems = items.length;
                
                return (
                  <div 
                    key={order.id} 
                    className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-card-enter ${
                      isSelected ? 'ring-2 ring-primary-500 dark:ring-primary-400 shadow-lg' : ''
                    } ${
                      pendingOrders.has(order.id!) ? 'opacity-75 bg-yellow-50/90 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700' : 
                      syncedOrders.has(order.id!) ? 'bg-green-50/90 dark:bg-green-900/30 border-green-200 dark:border-green-700' : ''
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Order Summary - Clickable */}
                    <div
                      onClick={() => {
                        if (isSelected && isMobileExpanded) {
                          setIsMobileExpanded(false);
                          setSelectedOrderForDetail(null);
                          setSelectedOrderItems([]);
                        } else {
                          setSelectedOrderForDetail(order);
                          setSelectedOrderItems(items);
                          setIsMobileExpanded(true);
                        }
                      }}
                      className="p-4 cursor-pointer transition-all duration-300 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white truncate text-base">
                              {order.customer}
                            </h4>
                            {pendingOrders.has(order.id!) && (
                              <div className="flex items-center text-yellow-600 dark:text-yellow-400 text-xs bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-full">
                                <Loader className="w-3 h-3 animate-spin mr-1" />
                                <span>Syncing...</span>
                              </div>
                            )}
                            {syncedOrders.has(order.id!) && (
                              <div className="flex items-center text-green-600 dark:text-green-400 text-xs bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                <span>Synced!</span>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">
                            #{order.id?.slice(-6)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
                            tabs.find(tab => tab.id === activeTab)?.bgColor
                          } ${tabs.find(tab => tab.id === activeTab)?.color}`}>
                            {totalItems} item{totalItems !== 1 ? 's' : ''}
                          </div>
                          <div className={`transform transition-transform duration-300 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}>
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              ₹{order.total.toFixed(2)}
                            </span>
                          </div>
                          {order.remainingAmount > 0 && (
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-red-500 dark:text-red-400">Due</span>
                              <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                                ₹{order.remainingAmount.toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Time</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {order.createdAt instanceof Date 
                                ? order.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : new Date(order.createdAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              }
                            </span>
                          </div>
                          {order.customerPhone && (
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Phone</span>
                              <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                                {order.customerPhone}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expandable Order Details */}
                    {isExpanded && selectedOrderForDetail && (
                      <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-4 animate-in slide-in-from-top duration-200">
                        {/* Order Header */}
                        <div className="mb-4">
                          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
                            Order Details
                          </h3>
                          
                          {selectedOrderForDetail.notes && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded p-2 mb-3">
                              <div className="flex items-start space-x-2">
                                <FileText className="w-3 h-3 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-yellow-700 dark:text-yellow-200">{selectedOrderForDetail.notes}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Order Items */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Items ({selectedOrderItems.length})
                          </h4>
                          
                          <div className="space-y-2">
                            {selectedOrderItems.map((item) => (
                              <div key={item.id} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                      {item.menuItemName}
                                    </h5>
                                    <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Qty:</span>
                                        <span className="font-semibold ml-1">{item.quantity}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Price:</span>
                                        <span className="ml-1">₹{item.unitPrice.toFixed(2)}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Total:</span>
                                        <span className="font-semibold ml-1 text-primary-600 dark:text-primary-400">
                                          ₹{item.totalPrice.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (selectedOrderForDetail) {
                                        handleDeleteItem(selectedOrderForDetail.id!, item.id, item.menuItemName);
                                      }
                                    }}
                                    className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors duration-200 flex-shrink-0"
                                    title="Delete item"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>

                                {item.notes && (
                                  <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded p-2 mb-2">
                                    <p className="text-xs text-gray-600 dark:text-gray-300">
                                      <span className="font-medium">Notes:</span> {item.notes}
                                    </p>
                                  </div>
                                )}

                                {/* Item Action Buttons */}
                                <div className="mt-2">
                                  {activeTab === 'requested' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedOrderForDetail) {
                                          handleStatusChange(selectedOrderForDetail.id!, item.id, 'preparing');
                                        }
                                      }}
                                      className="w-full btn-preparing"
                                    >
                                      Start Preparing
                                    </button>
                                  )}
                                  
                                  {activeTab === 'preparing' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedOrderForDetail) {
                                          handleStatusChange(selectedOrderForDetail.id!, item.id, 'completed');
                                        }
                                      }}
                                      className="w-full btn-complete"
                                    >
                                      Mark Complete
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            {/* Add Items Button - Mobile */}
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddItemsToOrder(selectedOrderForDetail);
                              }}
                              className="bg-white dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors duration-200 cursor-pointer"
                            >
                              <div className="flex items-center justify-center space-x-2">
                                <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                                  <Plus className="w-3 h-3 text-primary-600 dark:text-primary-400" />
                                </div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add Items</span>
                                {!posSimplified && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    activeTab === 'requested' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                                    activeTab === 'preparing' ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' :
                                    'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                                  }`}>
                                    As {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Order Summary & Actions */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                          <div className="bg-white dark:bg-gray-700 rounded-lg p-3 mb-3">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Order Summary</h4>
                            <div className="grid grid-cols-3 gap-4 text-xs">
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">Subtotal</div>
                                <div className="font-medium">₹{selectedOrderForDetail.total.toFixed(2)}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">Paid</div>
                                <div className="font-medium text-green-600 dark:text-green-400">₹{selectedOrderForDetail.paidAmount.toFixed(2)}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">Remaining</div>
                                <div className={`font-bold ${selectedOrderForDetail.remainingAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  ₹{selectedOrderForDetail.remainingAmount.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-2">
                            {selectedOrderForDetail.remainingAmount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePayment(selectedOrderForDetail);
                                }}
                                className="w-full btn-primary flex items-center justify-center space-x-2 text-sm"
                              >
                                <CreditCard className="w-4 h-4" />
                                <span>Add Payment</span>
                              </button>
                            )}
                            
                            {/* Receipt Options for completed orders that are fully paid */}
                            {activeTab === 'completed' && selectedOrderForDetail.remainingAmount <= 0 && (
                              <div onClick={(e) => e.stopPropagation()} className="space-y-2">
                                <div className="flex gap-2">
                                  {/* Print Receipt Button */}
                                  <PrintReceiptButton
                                    order={selectedOrderForDetail}
                                    variant="icon"
                                    size="sm"
                                    className="min-w-[40px]"
                                    onPrintError={(error) => console.error('Print error:', error)}
                                    onPrintSuccess={() => console.log('Receipt printed successfully')}
                                  />
                                  
                                  {/* WhatsApp Receipt Button */}
                                  <WhatsAppReceiptButton
                                    order={selectedOrderForDetail}
                                    cafeName={tenantData?.businessName || 'CafeLawn'}
                                    cafeAddress={tenantData?.address || `${tenantData?.businessName || 'Your Cafe'}, Your City, Your State`}
                                    cafePhone={tenantData?.phone || tenantData?.email || 'Contact: Not Available'}
                                    cafeInstagram={tenantData?.instagramHandle ? `@${tenantData.instagramHandle}` : `@${tenantData?.businessName?.toLowerCase().replace(/\s+/g, '') || 'cafelawn'}`}
                                    onPhoneUpdate={handlePhoneUpdate}
                                    variant="secondary"
                                    size="sm"
                                    className="min-w-[80px]"
                                  />
                                  
                                  {/* Call Button */}
                                  {selectedOrderForDetail.customerPhone && (
                                    <button
                                      onClick={() => window.open(`tel:${selectedOrderForDetail.customerPhone}`, '_blank')}
                                      className="min-w-[40px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-3 py-2 rounded-lg flex items-center justify-center transition-all duration-200 border border-gray-200 dark:border-gray-600"
                                      title="Call customer"
                                    >
                                      <Phone className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tablet+ View - Side by Side */}
        <div className="hidden tablet:flex flex-row gap-4 tablet:gap-6 min-h-[500px]">
          {/* Left Sidebar - Orders List */}
          <div className="w-full tablet:w-1/3 tablet-lg:w-1/4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col max-h-[500px]">
            {/* <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Orders
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                {orderGroups.length} order{orderGroups.length !== 1 ? 's' : ''}
              </p>
            </div> */}
            
            <div className="flex-1 overflow-y-auto">
              {orderGroups.map(({ order, items }, index) => {
                const isSelected = selectedOrderForDetail?.id === order.id;
                const totalItems = items.length;
                
                return (
                  <div
                    key={order.id}
                    onClick={() => {
                      setSelectedOrderForDetail(order);
                      setSelectedOrderItems(items);
                    }}
                    className={`p-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 animate-card-enter-tablet ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-l-primary-500' : ''
                    } ${
                      pendingOrders.has(order.id!) ? 'opacity-75 bg-yellow-50/90 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700' : 
                      syncedOrders.has(order.id!) ? 'bg-green-50/90 dark:bg-green-900/30 border-green-200 dark:border-green-700 animate-order-sync' : ''
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate text-sm">
                          {order.customer}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {order.id}
                        </p>
                        {/* Optimistic UI indicators for tablet view */}
                        {pendingOrders.has(order.id!) && (
                          <div className="flex items-center text-yellow-600 dark:text-yellow-400 text-xs mt-1">
                            <Loader className="w-3 h-3 animate-spin mr-1" />
                            <span>Syncing...</span>
                          </div>
                        )}
                        {syncedOrders.has(order.id!) && (
                          <div className="flex items-center text-green-600 dark:text-green-400 text-xs mt-1">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            <span>Synced!</span>
                          </div>
                        )}
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tabs.find(tab => tab.id === activeTab)?.bgColor
                      } ${tabs.find(tab => tab.id === activeTab)?.color}`}>
                        {totalItems}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-300">
                        ₹{order.total.toFixed(2)}
                      </span>
                      {order.remainingAmount > 0 && (
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          ₹{order.remainingAmount.toFixed(2)} due
                        </span>
                      )}
                    </div>
                    
                    {order.customerPhone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {order.customerPhone}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {totalItems} item{totalItems !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {order.createdAt instanceof Date 
                          ? order.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : new Date(order.createdAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Order Details */}
          <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col max-h-[500px]">
            {selectedOrderForDetail ? (
              <>
                {/* Order Header - Compact */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex-shrink-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        {selectedOrderForDetail.customer}
                      </h2>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                        <span>ID: {selectedOrderForDetail.id}</span>
                        {selectedOrderForDetail.customerPhone && (
                          <div className="flex items-center space-x-1">
                            <Phone className="w-3 h-3" />
                            <span>{selectedOrderForDetail.customerPhone}</span>
                          </div>
                        )}
                        {selectedOrderForDetail.tableNumber && (
                          <span>Table: {selectedOrderForDetail.tableNumber}</span>
                        )}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tabs.find(tab => tab.id === activeTab)?.bgColor
                    } ${tabs.find(tab => tab.id === activeTab)?.color}`}>
                      {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                    </div>
                  </div>

                  {selectedOrderForDetail.notes && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded p-2 mt-2">
                      <div className="flex items-start space-x-2">
                        <FileText className="w-3 h-3 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-yellow-700 dark:text-yellow-200">{selectedOrderForDetail.notes}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Items - Grid Layout */}
                <div className="flex-1 p-4 overflow-y-auto">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Order Items ({selectedOrderItems.length})
                  </h3>
                  
                  <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3">
                    {selectedOrderItems.map((item) => (
                      <div key={item.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                              {item.menuItemName}
                            </h4>
                            <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Qty:</span>
                                <span className="font-semibold ml-1">{item.quantity}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Price:</span>
                                <span className="ml-1">₹{item.unitPrice.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Total:</span>
                                <span className="font-semibold ml-1 text-primary-600 dark:text-primary-400">
                                  ₹{item.totalPrice.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteItem(selectedOrderForDetail.id!, item.id, item.menuItemName)}
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors duration-200 flex-shrink-0"
                            title="Delete item"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>

                        {item.notes && (
                          <div className="bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded p-2 mb-2">
                            <p className="text-xs text-gray-600 dark:text-gray-300">
                              <span className="font-medium">Notes:</span> {item.notes}
                            </p>
                          </div>
                        )}

                        {/* Item Action Buttons */}
                        <div className="mt-2">
                          {activeTab === 'requested' && (
                            <button
                              onClick={() => handleStatusChange(selectedOrderForDetail.id!, item.id, 'preparing')}
                              className="w-full btn-preparing"
                            >
                              Start Preparing
                            </button>
                          )}
                          
                          {activeTab === 'preparing' && (
                            <button
                              onClick={() => handleStatusChange(selectedOrderForDetail.id!, item.id, 'completed')}
                              className="w-full btn-complete"
                            >
                              Mark Complete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Items Button */}
                    <div 
                      onClick={() => handleAddItemsToOrder(selectedOrderForDetail)}
                      className="bg-white dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors duration-200 cursor-pointer group"
                    >
                      <div className="flex flex-col items-center justify-center h-full min-h-[100px]">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 group-hover:bg-primary-200 dark:group-hover:bg-primary-800 flex items-center justify-center mb-2 transition-colors duration-200">
                          <Plus className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-300 text-center transition-colors duration-200">
                          Add Items
                        </span>
                        {!posSimplified && (
                          <span className={`text-xs mt-1 px-2 py-0.5 rounded-full font-medium ${
                            activeTab === 'requested' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                            activeTab === 'preparing' ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' :
                            'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                          }`}>
                            As {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary & Actions - Compact */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700 flex-shrink-0">
                  <div className="flex flex-col tablet:flex-row gap-4">
                    {/* Order Summary */}
                    <div className="bg-white dark:bg-gray-600 rounded-lg p-3 flex-1">
                      <div className="grid grid-cols-3 gap-4 text-xs">
                        <div className="text-center">
                          <div className="text-gray-500 dark:text-gray-400">Subtotal</div>
                          <div className="font-medium">₹{selectedOrderForDetail.total.toFixed(2)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500 dark:text-gray-400">Paid</div>
                          <div className="font-medium text-green-600 dark:text-green-400">₹{selectedOrderForDetail.paidAmount.toFixed(2)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500 dark:text-gray-400">Remaining</div>
                          <div className={`font-bold ${selectedOrderForDetail.remainingAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                            ₹{selectedOrderForDetail.remainingAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 tablet:w-48">
                      {selectedOrderForDetail.remainingAmount > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePayment(selectedOrderForDetail);
                          }}
                          className="btn-primary flex items-center justify-center space-x-2 text-sm"
                        >
                          <CreditCard className="w-4 h-4" />
                          <span>Add Payment</span>
                        </button>
                      )}
                      
                      {/* Receipt Options for completed orders that are fully paid */}
                      {activeTab === 'completed' && selectedOrderForDetail.remainingAmount <= 0 && (
                        <div className="flex gap-2">
                          {/* Print Receipt Button */}
                          <PrintReceiptButton
                            order={selectedOrderForDetail}
                            variant="icon"
                            size="sm"
                            className="min-w-[40px]"
                            onPrintError={(error) => console.error('Print error:', error)}
                            onPrintSuccess={() => console.log('Receipt printed successfully')}
                          />
                          
                          {/* WhatsApp Receipt Button */}
                          <WhatsAppReceiptButton
                            order={selectedOrderForDetail}
                            cafeName={tenantData?.businessName || 'CafeLawn'}
                            cafeAddress={tenantData?.address || `${tenantData?.businessName || 'Your Cafe'}, Your City, Your State`}
                            cafePhone={tenantData?.phone || tenantData?.email || 'Contact: Not Available'}
                            cafeInstagram={tenantData?.instagramHandle ? `@${tenantData.instagramHandle}` : `@${tenantData?.businessName?.toLowerCase().replace(/\s+/g, '') || 'cafelawn'}`}
                            onPhoneUpdate={handlePhoneUpdate}
                            variant="secondary"
                            size="sm"
                            className="min-w-[80px]"
                          />
                          
                          {/* Call Button */}
                          {selectedOrderForDetail.customerPhone && (
                            <button
                              onClick={() => window.open(`tel:${selectedOrderForDetail.customerPhone}`, '_blank')}
                              className="min-w-[40px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-3 py-2 rounded-lg flex items-center justify-center transition-all duration-200 border border-gray-200 dark:border-gray-600"
                              title="Call customer"
                            >
                              <Phone className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <div className="text-gray-400 dark:text-gray-500 mb-4">
                    {activeTab === 'requested' && <AlertCircle className="w-12 h-12 mx-auto" />}
                    {activeTab === 'preparing' && <Clock className="w-12 h-12 mx-auto" />}
                    {activeTab === 'completed' && <CheckCircle className="w-12 h-12 mx-auto" />}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Select an order to view details
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Choose an order from the left panel to see its details and manage items.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'requested':
        return requestedItems;
      case 'preparing':
        return preparingItems;
      case 'completed':
        // Apply completed filter and search
        let filteredCompletedItems = completedItems;
        
        // Apply payment filter
        if (completedFilter === 'due') {
          // Show only completed orders that have remaining balance (due payments)
          filteredCompletedItems = filteredCompletedItems.filter(({ order }) => order.remainingAmount > 0);
        }
        
        // Apply search filter
        if (completedSearchTerm.trim()) {
          filteredCompletedItems = filteredCompletedItems.filter(({ order }) => 
            order.customer.toLowerCase().includes(completedSearchTerm.toLowerCase()) ||
            (order.id && order.id.toLowerCase().includes(completedSearchTerm.toLowerCase())) ||
            (order.customerPhone && order.customerPhone.toLowerCase().includes(completedSearchTerm.toLowerCase())) ||
            (order.tableNumber && order.tableNumber.toLowerCase().includes(completedSearchTerm.toLowerCase()))
          );
        }
        
        return filteredCompletedItems;
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Date Filter Section - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-3 md:p-4">
        <div className="flex flex-col space-y-2 sm:space-y-3 md:space-y-4">
          {/* Header Row - Compact on mobile */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-900 dark:text-white">Filter Orders</h3>
            </div>
            
            {/* Date Filter Tabs - Compact toggle buttons */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md sm:rounded-lg p-0.5 sm:p-1">
              <button
                onClick={() => setDateFilter('today')}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  dateFilter === 'today'
                    ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                <span className="hidden sm:inline">Today</span>
                <span className="sm:hidden">Today</span>
              </button>
              <button
                onClick={() => setDateFilter('range')}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  dateFilter === 'range'
                    ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <span className="hidden sm:inline">Date Range</span>
                <span className="sm:hidden">Range</span>
              </button>
            </div>
          </div>
          
          {/* Additional Filter Elements - Compact on mobile */}
          <div className="flex flex-col space-y-2 sm:space-y-3">
            {/* Date Range Inputs - Compact on mobile */}
            {dateFilter === 'range' && (
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1 sm:flex-none text-xs sm:text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-2 sm:px-3 py-1.5 sm:py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-1">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="flex-1 sm:flex-none text-xs sm:text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-2 sm:px-3 py-1.5 sm:py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Current Filter Display - Compact on mobile */}
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg">
              {dateFilter === 'today' ? (
                <span className="flex items-center">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-green-500" />
                  Today's orders
                </span>
              ) : (
                <span className="flex items-center">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-blue-500" />
                  {startDate === endDate 
                    ? `Orders for ${new Date(startDate).toLocaleDateString()}`
                    : `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
                  }
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Hidden in Simplified Mode */}
      {!posSimplified && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-1 sm:space-x-2 md:space-x-4 lg:space-x-8 overflow-x-auto px-1 sm:px-2 md:px-0 scrollbar-hide">
            {/* Mobile scroll indicator */}
            <div className="sm:hidden flex-shrink-0 w-1 h-full bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent rounded-full opacity-50 scroll-indicator"></div>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const count = tab.id === 'requested' ? requestedItems.length :
                           tab.id === 'preparing' ? preparingItems.length :
                           getCurrentItems().length; // Use filtered count for completed tab
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-2 sm:py-3 md:py-4 px-1.5 sm:px-2 md:px-3 border-b-2 font-medium text-xs sm:text-sm md:text-base flex items-center space-x-1 sm:space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name === 'Requested' ? 'Req' : tab.name === 'Preparing' ? 'Prep' : 'Comp'}</span>
                  <span className={`px-1 sm:px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium min-w-[1.25rem] text-center ${
                    activeTab === tab.id ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
            {/* Mobile scroll indicator at the end */}
            <div className="sm:hidden flex-shrink-0 w-1 h-full bg-gradient-to-l from-transparent via-gray-300 dark:via-gray-600 to-transparent rounded-full opacity-50 scroll-indicator"></div>
          </nav>
        </div>
      )}

      {/* Tab Content */}
      <div>
        {/* Completed Tab Filter - In Simplified Mode: No label, just clean filter */}
        {activeTab === 'completed' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg sm:shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-3 md:p-4 mb-3 sm:mb-4">
            <div className="flex flex-row items-center gap-2 sm:gap-3">
              {/* Compact Search Input - Mobile Optimized */}
              <div className={`relative transition-all duration-300 ${
                isMobile && isSearchExpanded ? 'flex-1' : 'flex-1 min-w-0'
              }`}>
                <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input 
                  type="text"
                  value={completedSearchTerm}
                  onChange={(e) => setCompletedSearchTerm(e.target.value)}
                  onFocus={() => isMobile && setIsSearchExpanded(true)}
                  onBlur={() => isMobile && setIsSearchExpanded(false)}
                  placeholder={posSimplified ? (isMobile ? "Search..." : "Search orders...") : (isMobile ? "Search..." : "Search completed orders...")}
                  className="w-full pl-8 sm:pl-10 pr-8 sm:pr-4 py-1.5 sm:py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base transition-all duration-200"
                />
                {completedSearchTerm && (
                  <button
                    onClick={() => setCompletedSearchTerm('')}
                    className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                {/* Mobile search hint */}
                {isMobile && !isSearchExpanded && !completedSearchTerm && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded text-xs text-gray-500 dark:text-gray-400 opacity-60">
                      Tap to search
                    </div>
                  </div>
                )}
              </div>
              
              {/* Compact Filter Dropdown - Mobile Optimized */}
              <div className={`flex-shrink-0 transition-all duration-300 ${
                isMobile && isSearchExpanded ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
              }`}>
                <select 
                  value={completedFilter}
                  onChange={(e) => setCompletedFilter(e.target.value as 'all' | 'due')}
                  className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs sm:text-sm min-w-[120px] sm:min-w-[160px]"
                >
                  <option value="all" className="text-xs sm:text-sm">
                    {posSimplified ? (isMobile ? 'All Orders' : 'All Orders') : (isMobile ? 'All Orders' : 'All Completed Orders')}
                  </option>
                  <option value="due" className="text-xs sm:text-sm">
                    {isMobile ? 'Pending' : 'Pending Payments'}
                  </option>
                </select>
              </div>
              
              {/* Compact Status Indicator - Desktop Only - Hidden in simplified mode */}
              {!posSimplified && (
                <div className="hidden sm:flex text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  {completedFilter === 'all' ? 'Showing all completed orders' : 'Showing orders with pending payments'}
                </div>
              )}
              
              {/* Mobile Status Badge - Only show on mobile when search not expanded */}
              <div className={`sm:hidden flex-shrink-0 transition-all duration-300 ${
                isMobile && isSearchExpanded ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
              }`}>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  completedFilter === 'all' 
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400' 
                    : 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400'
                }`}>
                  {completedFilter === 'all' ? 'All' : 'Due'}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {renderOrderItems(getCurrentItems())}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddOrderModal
          currentTab={posSimplified ? 'completed' : (activeTab as 'requested' | 'preparing' | 'completed')}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddOrder}
          onOptimisticOrderCreate={addOrderOptimistically}
          posSimplified={posSimplified}
        />
      )}

      {showAddItemModal && orderToAddItems && (
        <AddItemToOrderModal
          order={orderToAddItems}
          currentTab={posSimplified ? 'completed' : (activeTab as 'requested' | 'preparing' | 'completed')}
          onClose={() => setShowAddItemModal(false)}
          onSuccess={handleAddItemSuccess}
          onOptimisticItemsAdd={addItemsToOrderOptimistically}
          posSimplified={posSimplified}
        />
      )}

      {showPaymentModal && selectedOrder && (
        <PaymentModal
          order={selectedOrder}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentComplete}
        />
      )}
    </div>
  );
};

export default Orders; 